/**
 * TanStack Query factory functions for the Agent Commission Engine.
 *
 * Tables (introduced in migration 20261028000000_agent_commission_engine.sql):
 *   - agent_commission_rates    8-row bucket rate card
 *   - agent_commission_lines    one row per qualifying order_line accrual
 *   - agent_commission_payouts  one row per admin "close period" action
 *
 * RLS already scopes the visible rows correctly:
 *   - Agents see only their own rows (agent_id = auth.uid())
 *   - Admins see all rows
 *
 * The optional `agentId` arg on each factory is therefore a *filter*, not a
 * security boundary. Pass it to scope an admin view to a single agent;
 * pass null and rely on RLS for an agent's self-view.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

/* -------------------------------------------------------------------------- */
/*  Shared types                                                              */
/* -------------------------------------------------------------------------- */

export type CommissionBucket = Database['public']['Enums']['commission_bucket'];

export interface AgentCommissionSummary {
  /** Sum of line_commission_aed for the current calendar month (any confirmation). */
  this_month_accrued_aed: number;
  /** Sum of line_commission_aed for the current calendar month where is_confirmed=true. */
  this_month_confirmed_aed: number;
  /** Pending, not yet confirmed (is_confirmed=false AND payout_id IS NULL). */
  pending_unconfirmed_aed: number;
  /** Pending, confirmed but not paid (is_confirmed=true AND payout_id IS NULL). */
  pending_unpaid_aed: number;
  /** Total paid in the last 12 months (payouts where status='paid' AND closed_at >= now-12mo). */
  paid_12mo_aed: number;
  /** Distinct clinics with at least one commission line accrued in the last 12 months. */
  active_clinics_count: number;
}

export interface AgentCommissionMonthlyRow {
  period: string; // YYYY-MM
  bucket: CommissionBucket;
  total_aed: number;
}

export interface AgentClinicTimelineRow {
  clinic_id: string;
  clinic_name: string;
  first_submitted_at: string | null;
  /** first_submitted_at + 12 months. Null when the clinic has never submitted. */
  eligibility_end_at: string | null;
  /** ceil((eligibility_end_at - now) / 30 days), clamped to 0. Null when never submitted. */
  months_remaining: number | null;
  total_accrued_aed: number;
  total_paid_aed: number;
  monthly_sparkline: Array<{ period: string; total_aed: number }>;
}

export interface AgentCommissionLineRow {
  id: string;
  agent_id: string;
  order_line_id: string;
  order_id: string;
  clinic_id: string;
  bucket: CommissionBucket;
  qty: number;
  unit_price_aed: number;
  unit_commission_aed: number;
  line_commission_aed: number;
  tax_reserve_aed: number;
  period_year_month: string;
  is_confirmed: boolean;
  confirmed_at: string | null;
  accrued_at: string;
  payout_id: string | null;
  order: {
    id: string;
    order_number: string;
    submitted_at: string | null;
    status: string;
  } | null;
  clinic: { id: string; name: string } | null;
  catalog_sku: string | null;
  payout: { id: string; status: 'pending' | 'paid' } | null;
}

export interface AgentCommissionLinesResult {
  rows: AgentCommissionLineRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AgentCommissionRateRow {
  bucket: CommissionBucket;
  target_price_aed: number;
  floor_price_aed: number;
  target_commission_aed: number;
  active: boolean;
  effective_from: string;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

/**
 * One row from public.agent_clinic_commission_agreements scoped to a
 * single (agent, clinic) pair. Surfaced read-only on the agent "Your
 * clinics" cards as the negotiated price and commission per bucket.
 *
 * Visible to: admin (any pair) + the agent who owns the row (RLS in
 * migration 20261122000200 widens the SELECT policy with
 * agent_id = auth.uid()).
 *
 * The agreements table is not yet present in the generated Database
 * types, so the query factory below uses the same untyped-client cast
 * pattern as commissions.ts (asUntyped). Once types are regenerated
 * the cast can be dropped.
 */
export interface AgentClinicAgreementRateRow {
  clinic_id: string;
  bucket: CommissionBucket;
  agreed_price_aed: number;
  commission_aed: number;
  notes: string | null;
  updated_at: string;
}

/**
 * Per-clinic commission rate override row, sourced from the
 * clinic_commission_overrides table introduced in
 * 20261030000000_commission_buckets_v2.sql.
 *
 * Shape mirrors AgentCommissionRateRow with two differences:
 *   , adds clinic_id (compound PK with bucket).
 *   , no effective_from column. The override is in effect from the
 *     moment active=true and revoked the moment it's deleted or
 *     active=false; we don't model historical effective dates because
 *     overrides are commercial negotiations, not legal contracts.
 *
 * Visible to: admin (any clinic) + the agent who acquired the clinic.
 * Writable by: admin only. The agent UI uses this shape read-only.
 */
export interface ClinicCommissionOverrideRow {
  clinic_id: string;
  bucket: CommissionBucket;
  target_price_aed: number;
  floor_price_aed: number;
  target_commission_aed: number;
  active: boolean;
  notes: string | null;
  updated_at: string;
  updated_by: string | null;
}

/* -------------------------------------------------------------------------- */
/*  Query keys                                                                */
/* -------------------------------------------------------------------------- */

export const agentCommissionKeys = {
  all: ['agent-commissions'] as const,
  summary: (agentId: string | null) =>
    [...agentCommissionKeys.all, 'summary', agentId] as const,
  monthly: (agentId: string | null, fromMonth?: string, toMonth?: string) =>
    [
      ...agentCommissionKeys.all,
      'monthly',
      agentId,
      fromMonth ?? null,
      toMonth ?? null,
    ] as const,
  clinicTimeline: (agentId: string | null) =>
    [...agentCommissionKeys.all, 'clinic-timeline', agentId] as const,
  lines: (
    agentId: string | null,
    period: string | undefined,
    clinicId: string | undefined,
    page: number,
    pageSize: number,
  ) =>
    [
      ...agentCommissionKeys.all,
      'lines',
      agentId,
      period ?? null,
      clinicId ?? null,
      page,
      pageSize,
    ] as const,
  rates: () => [...agentCommissionKeys.all, 'rates'] as const,
  clinicOverrides: (clinicId: string) =>
    [...agentCommissionKeys.all, 'clinic-overrides', clinicId] as const,
  agentClinicAgreements: (agentId: string | null, clinicId: string) =>
    [
      ...agentCommissionKeys.all,
      'agent-clinic-agreements',
      agentId,
      clinicId,
    ] as const,
};

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

function currentMonthString(d: Date = new Date()): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function addMonths(d: Date, n: number): Date {
  const r = new Date(d.getTime());
  r.setUTCMonth(r.getUTCMonth() + n);
  return r;
}

function monthString(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function sumLineAed(
  rows: ReadonlyArray<{ line_commission_aed?: unknown }>,
): number {
  return rows.reduce((acc, r) => acc + Number(r.line_commission_aed ?? 0), 0);
}

/* -------------------------------------------------------------------------- */
/*  1. agentCommissionSummaryQuery                                            */
/* -------------------------------------------------------------------------- */

/**
 * Returns aggregated counters for the agent dashboard KPI strip.
 *
 * Aggregation strategy: client-side sums over a few small filtered SELECTs.
 *
 * Rationale: PostgREST aggregate syntax (`select=sum(col)`) requires the
 * `pgrst.aggregate` flag and produces unsightly TypeScript. The commission
 * lines for any single agent are small (hundreds per year, maybe low
 * thousands ever) so fetching the relevant `line_commission_aed` columns
 * over a handful of parallel queries is cheap and avoids an RPC round-trip
 * plus the need to extend the migration with a summary function. If/when the
 * dataset grows past ~5k rows per agent we can swap to a SQL summary RPC
 * with no consumer changes.
 *
 * Five parallel queries:
 *   a) current-month accrued (filter period_year_month = YYYY-MM)
 *   b) current-month confirmed (a + is_confirmed=true)
 *   c) pending unconfirmed (is_confirmed=false AND payout_id IS NULL)
 *   d) pending unpaid (is_confirmed=true AND payout_id IS NULL)
 *   e) last-12-months distinct clinic_ids (then count client-side)
 * Plus one query against agent_commission_payouts for paid-12mo.
 */
export function agentCommissionSummaryQuery(
  client: SupabaseClient<Database>,
  opts: { agentId: string | null },
) {
  const { agentId } = opts;
  return {
    queryKey: agentCommissionKeys.summary(agentId),
    queryFn: async (): Promise<AgentCommissionSummary> => {
      const period = currentMonthString();
      const twelveMonthsAgo = addMonths(new Date(), -12).toISOString();

      // a + b: current-month rows (we partition is_confirmed client-side).
      let qCurrent = client
        .from('agent_commission_lines')
        .select('line_commission_aed, is_confirmed')
        .eq('period_year_month', period);
      if (agentId) qCurrent = qCurrent.eq('agent_id', agentId);

      // c: pending unconfirmed (is_confirmed=false AND payout_id IS NULL)
      let qUnconf = client
        .from('agent_commission_lines')
        .select('line_commission_aed')
        .eq('is_confirmed', false)
        .is('payout_id', null);
      if (agentId) qUnconf = qUnconf.eq('agent_id', agentId);

      // d: pending unpaid (is_confirmed=true AND payout_id IS NULL)
      let qUnpaid = client
        .from('agent_commission_lines')
        .select('line_commission_aed')
        .eq('is_confirmed', true)
        .is('payout_id', null);
      if (agentId) qUnpaid = qUnpaid.eq('agent_id', agentId);

      // e: distinct clinics over last 12 months
      let qLast12 = client
        .from('agent_commission_lines')
        .select('clinic_id')
        .gte('accrued_at', twelveMonthsAgo);
      if (agentId) qLast12 = qLast12.eq('agent_id', agentId);

      // f: paid in the last 12 months from payouts.net_aed
      let qPaid = client
        .from('agent_commission_payouts')
        .select('net_aed')
        .eq('status', 'paid')
        .gte('closed_at', twelveMonthsAgo);
      if (agentId) qPaid = qPaid.eq('agent_id', agentId);

      const [
        currentMonthRes,
        pendingUnconfRes,
        pendingUnpaidRes,
        last12moRes,
        paid12moRes,
      ] = await Promise.all([qCurrent, qUnconf, qUnpaid, qLast12, qPaid]);

      for (const r of [
        currentMonthRes,
        pendingUnconfRes,
        pendingUnpaidRes,
        last12moRes,
        paid12moRes,
      ]) {
        if (r.error) throw new Error(r.error.message);
      }

      const currentRows = currentMonthRes.data ?? [];
      const this_month_accrued_aed = sumLineAed(currentRows);
      const this_month_confirmed_aed = sumLineAed(
        currentRows.filter((r) => r.is_confirmed === true),
      );
      const pending_unconfirmed_aed = sumLineAed(pendingUnconfRes.data ?? []);
      const pending_unpaid_aed = sumLineAed(pendingUnpaidRes.data ?? []);
      const paid_12mo_aed = (paid12moRes.data ?? []).reduce(
        (acc, r) => acc + Number(r.net_aed ?? 0),
        0,
      );
      const active_clinics_count = new Set(
        (last12moRes.data ?? []).map((r) => r.clinic_id),
      ).size;

      return {
        this_month_accrued_aed,
        this_month_confirmed_aed,
        pending_unconfirmed_aed,
        pending_unpaid_aed,
        paid_12mo_aed,
        active_clinics_count,
      };
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  2. agentCommissionMonthlyQuery                                            */
/* -------------------------------------------------------------------------- */

/**
 * Returns one row per (period_year_month, bucket) with summed line_commission_aed.
 *
 * Aggregation strategy: pull the (period_year_month, bucket, line_commission_aed)
 * tuples for the requested window and aggregate client-side. PostgREST's
 * GROUP BY support is gated behind the `pgrst.aggregate` flag and would
 * require either an RPC or a SQL view; neither is in scope for the SDK
 * task. Per-agent volume over a 12-month window is small (a few hundred
 * rows max), so the in-JS sum is fine.
 *
 * Defaults: fromMonth = 11 months before the current month (so the chart
 * shows 12 buckets including the current month); toMonth = current month.
 */
export function agentCommissionMonthlyQuery(
  client: SupabaseClient<Database>,
  opts: { agentId: string | null; fromMonth?: string; toMonth?: string },
) {
  const { agentId, fromMonth, toMonth } = opts;
  return {
    queryKey: agentCommissionKeys.monthly(agentId, fromMonth, toMonth),
    queryFn: async (): Promise<AgentCommissionMonthlyRow[]> => {
      const now = new Date();
      const effectiveTo = toMonth ?? monthString(now);
      const effectiveFrom = fromMonth ?? monthString(addMonths(now, -11));

      let q = client
        .from('agent_commission_lines')
        .select('period_year_month, bucket, line_commission_aed')
        .gte('period_year_month', effectiveFrom)
        .lte('period_year_month', effectiveTo);
      if (agentId) q = q.eq('agent_id', agentId);

      const { data, error } = await q;
      if (error) throw new Error(error.message);

      const grouped = new Map<string, AgentCommissionMonthlyRow>();
      for (const row of data ?? []) {
        const key = `${row.period_year_month}|${row.bucket}`;
        const existing = grouped.get(key);
        const inc = Number(row.line_commission_aed ?? 0);
        if (existing) {
          existing.total_aed += inc;
        } else {
          grouped.set(key, {
            period: row.period_year_month,
            bucket: row.bucket,
            total_aed: inc,
          });
        }
      }
      return Array.from(grouped.values()).sort((a, b) => {
        if (a.period === b.period) return a.bucket.localeCompare(b.bucket);
        return a.period.localeCompare(b.period);
      });
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  3. agentClinicTimelineQuery                                               */
/* -------------------------------------------------------------------------- */

/**
 * Returns per-clinic eligibility windows for the agent dashboard timeline.
 *
 * Aggregation strategy: three queries + client-side stitch.
 *   q1: list of clinics this agent acquired (acquired_by_agent_id = agentId,
 *       or all rows where acquired_by_agent_id IS NOT NULL when agentId is
 *       null and the caller is admin, RLS handles the latter).
 *   q2: MIN(submitted_at) per clinic, taken from orders for the same clinics.
 *   q3: agent_commission_lines for those clinics joined with the payout row
 *       so we can split accrued vs paid and build the monthly sparkline.
 *
 * No GROUP BY in PostgREST, so all aggregation happens in JS. Per-agent
 * volume is small (dozens of clinics, hundreds of orders); this is cheap.
 *
 * If `acquired_by_agent_id` is null we cannot reverse-resolve clinics from
 * commission lines alone (the agent could have zero lines yet), so we
 * always read from `clinics` as the source of truth for *which* clinics
 * belong to the agent.
 */
export function agentClinicTimelineQuery(
  client: SupabaseClient<Database>,
  opts: { agentId: string | null },
) {
  const { agentId } = opts;
  return {
    queryKey: agentCommissionKeys.clinicTimeline(agentId),
    queryFn: async (): Promise<AgentClinicTimelineRow[]> => {
      // q1: clinics owned by this agent
      let clinicsQ = client.from('clinics').select('id, name');
      if (agentId) {
        clinicsQ = clinicsQ.eq('acquired_by_agent_id', agentId);
      } else {
        clinicsQ = clinicsQ.not('acquired_by_agent_id', 'is', null);
      }
      const { data: clinicsData, error: clinicsErr } = await clinicsQ;
      if (clinicsErr) throw new Error(clinicsErr.message);
      const clinics = clinicsData ?? [];
      if (clinics.length === 0) return [];
      const clinicIds = clinics.map((c) => c.id);

      // q2 + q3 in parallel
      const [ordersRes, linesRes, payoutsRes] = await Promise.all([
        // q2: orders submitted_at per clinic
        client
          .from('orders')
          .select('clinic_id, submitted_at')
          .in('clinic_id', clinicIds)
          .not('submitted_at', 'is', null),
        // q3a: commission lines for the sparkline + accrued totals
        client
          .from('agent_commission_lines')
          .select(
            'clinic_id, period_year_month, line_commission_aed, payout_id',
          )
          .in('clinic_id', clinicIds),
        // q3b: payouts so we can know which payout_ids are paid
        client.from('agent_commission_payouts').select('id, status'),
      ]);
      if (ordersRes.error) throw new Error(ordersRes.error.message);
      if (linesRes.error) throw new Error(linesRes.error.message);
      if (payoutsRes.error) throw new Error(payoutsRes.error.message);

      // Map clinic_id -> first_submitted_at
      const firstByClinic = new Map<string, string>();
      for (const o of ordersRes.data ?? []) {
        if (!o.submitted_at) continue;
        const existing = firstByClinic.get(o.clinic_id);
        if (!existing || o.submitted_at < existing) {
          firstByClinic.set(o.clinic_id, o.submitted_at);
        }
      }

      // Map payout_id -> status (only paid matters for total_paid_aed)
      const paidPayoutIds = new Set(
        (payoutsRes.data ?? [])
          .filter((p) => p.status === 'paid')
          .map((p) => p.id),
      );

      // Aggregate lines per clinic
      const accruedByClinic = new Map<string, number>();
      const paidByClinic = new Map<string, number>();
      const sparkByClinic = new Map<string, Map<string, number>>();
      for (const ln of linesRes.data ?? []) {
        const amt = Number(ln.line_commission_aed ?? 0);
        accruedByClinic.set(
          ln.clinic_id,
          (accruedByClinic.get(ln.clinic_id) ?? 0) + amt,
        );
        if (ln.payout_id && paidPayoutIds.has(ln.payout_id)) {
          paidByClinic.set(
            ln.clinic_id,
            (paidByClinic.get(ln.clinic_id) ?? 0) + amt,
          );
        }
        let monthly = sparkByClinic.get(ln.clinic_id);
        if (!monthly) {
          monthly = new Map<string, number>();
          sparkByClinic.set(ln.clinic_id, monthly);
        }
        monthly.set(
          ln.period_year_month,
          (monthly.get(ln.period_year_month) ?? 0) + amt,
        );
      }

      const nowMs = Date.now();
      const MS_PER_DAY = 86_400_000;
      const rows: AgentClinicTimelineRow[] = clinics.map((c) => {
        const firstIso = firstByClinic.get(c.id) ?? null;
        let eligibilityEndIso: string | null = null;
        let monthsRemaining: number | null = null;
        if (firstIso) {
          const endDate = addMonths(new Date(firstIso), 12);
          eligibilityEndIso = endDate.toISOString();
          const diffDays = (endDate.getTime() - nowMs) / MS_PER_DAY;
          monthsRemaining = Math.max(0, Math.ceil(diffDays / 30));
        }
        const monthly = sparkByClinic.get(c.id);
        const monthly_sparkline = monthly
          ? Array.from(monthly.entries())
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([period, total_aed]) => ({ period, total_aed }))
          : [];
        return {
          clinic_id: c.id,
          clinic_name: c.name,
          first_submitted_at: firstIso,
          eligibility_end_at: eligibilityEndIso,
          months_remaining: monthsRemaining,
          total_accrued_aed: accruedByClinic.get(c.id) ?? 0,
          total_paid_aed: paidByClinic.get(c.id) ?? 0,
          monthly_sparkline,
        };
      });

      // Sort so clinics with the most accrued show first (most useful default).
      rows.sort((a, b) => b.total_accrued_aed - a.total_accrued_aed);
      return rows;
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  4. agentCommissionLinesQuery                                              */
/* -------------------------------------------------------------------------- */

/**
 * Paginated drill-down list of commission lines with joined order + clinic +
 * payout context. Used by the commission lines table on the agent dashboard
 * (and the admin "lines for agent X" view).
 *
 * Filters:
 *   - agentId: optional explicit agent filter (RLS already scopes for agents)
 *   - period:  exact match against period_year_month (YYYY-MM)
 *   - clinicId: exact match against clinic_id
 *
 * Pagination: .range(from, to) with `count: 'exact'` so the table footer
 * can show "showing X of Y". Default pageSize = 50.
 *
 * Joins via PostgREST embedding so we don't need a SQL view. order_line_id
 * is followed inline to surface the catalog_sku next to each line.
 */
export function agentCommissionLinesQuery(
  client: SupabaseClient<Database>,
  opts: {
    agentId: string | null;
    period?: string;
    clinicId?: string;
    page?: number;
    pageSize?: number;
  },
) {
  const { agentId, period, clinicId } = opts;
  const page = Math.max(0, opts.page ?? 0);
  const pageSize = Math.max(1, Math.min(opts.pageSize ?? 50, 500));
  return {
    queryKey: agentCommissionKeys.lines(agentId, period, clinicId, page, pageSize),
    queryFn: async (): Promise<AgentCommissionLinesResult> => {
      const from = page * pageSize;
      const to = from + pageSize - 1;
      // PostgREST embedded selects return shapes the generated types cannot
      // infer past a single hop, so we type the projection result manually
      // and feed it through the field-by-field mappers below.
      const select =
        'id, agent_id, order_line_id, order_id, clinic_id, bucket, qty,' +
        ' unit_price_aed, unit_commission_aed, line_commission_aed, tax_reserve_aed,' +
        ' period_year_month, is_confirmed, confirmed_at, accrued_at, payout_id,' +
        ' order:orders(id, order_number, submitted_at, status),' +
        ' clinic:clinics(id, name),' +
        ' order_line:order_lines(catalog_sku),' +
        ' payout:agent_commission_payouts(id, status)';

      let q = client
        .from('agent_commission_lines')
        .select(select, { count: 'exact' });
      if (agentId) q = q.eq('agent_id', agentId);
      if (period) q = q.eq('period_year_month', period);
      if (clinicId) q = q.eq('clinic_id', clinicId);
      const ordered = q
        .order('accrued_at', { ascending: false })
        .range(from, to);

      const { data, error, count } = await ordered;
      if (error) throw new Error(error.message);

      // PostgREST cannot infer a return shape from a multi-line embedded
      // select stored in a variable (it returns GenericStringError), so we
      // narrow through unknown into a Record and let the field-by-field
      // mappers below validate each value at runtime.
      const rows: AgentCommissionLineRow[] = (
        (data ?? []) as unknown as Array<Record<string, unknown>>
      ).map((r) => {
        const orderLine = (r.order_line ?? null) as
          | { catalog_sku: string | null }
          | null;
        return {
          id: String(r.id),
          agent_id: String(r.agent_id),
          order_line_id: String(r.order_line_id),
          order_id: String(r.order_id),
          clinic_id: String(r.clinic_id),
          bucket: r.bucket as CommissionBucket,
          qty: Number(r.qty ?? 0),
          unit_price_aed: Number(r.unit_price_aed ?? 0),
          unit_commission_aed: Number(r.unit_commission_aed ?? 0),
          line_commission_aed: Number(r.line_commission_aed ?? 0),
          tax_reserve_aed: Number(r.tax_reserve_aed ?? 0),
          period_year_month: String(r.period_year_month),
          is_confirmed: Boolean(r.is_confirmed),
          confirmed_at: (r.confirmed_at ?? null) as string | null,
          accrued_at: String(r.accrued_at),
          payout_id: (r.payout_id ?? null) as string | null,
          order: (r.order ?? null) as AgentCommissionLineRow['order'],
          clinic: (r.clinic ?? null) as AgentCommissionLineRow['clinic'],
          catalog_sku: orderLine?.catalog_sku ?? null,
          payout: (r.payout ?? null) as AgentCommissionLineRow['payout'],
        };
      });
      return {
        rows,
        total: count ?? rows.length,
        page,
        pageSize,
      };
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  5. agentCommissionRatesQuery                                              */
/* -------------------------------------------------------------------------- */

/**
 * Read-only fetch of the 8 commission rate rows. Used by:
 *   - admin "commission settings" page (editing)
 *   - agent dashboard "how is my commission calculated?" tooltip
 *
 * Returns rows ordered by bucket name so the UI is stable.
 */
export function agentCommissionRatesQuery(client: SupabaseClient<Database>) {
  return {
    queryKey: agentCommissionKeys.rates(),
    queryFn: async (): Promise<AgentCommissionRateRow[]> => {
      const { data, error } = await client
        .from('agent_commission_rates')
        .select(
          'bucket, target_price_aed, floor_price_aed, target_commission_aed, active, effective_from, notes, updated_at, updated_by',
        )
        .order('bucket', { ascending: true });
      if (error) throw new Error(error.message);
      // Exclude the sentinel 'none' bucket from every UI consumer
      // (admin rate card editor, agent dashboard rate tooltip, clinic
      // override editor). 'none' lives in the enum / rate table so
      // historical commission lines still resolve a label via
      // bucketLabel(), but it is never an editable bucket: it
      // represents the non-commissioned catalog branch, not a
      // configurable rate.
      return (data ?? [])
        .filter((r) => r.bucket !== 'none')
        .map((r) => ({
          bucket: r.bucket,
          target_price_aed: Number(r.target_price_aed ?? 0),
          floor_price_aed: Number(r.floor_price_aed ?? 0),
          target_commission_aed: Number(r.target_commission_aed ?? 0),
          active: Boolean(r.active),
          effective_from: String(r.effective_from),
          notes: r.notes,
          updated_at: String(r.updated_at),
          updated_by: r.updated_by,
        }));
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  6. clinicCommissionOverridesQuery                                         */
/* -------------------------------------------------------------------------- */

/**
 * Read-only fetch of the per-clinic commission override rows for a
 * single clinic. Returns 0..N rows (one per bucket the admin has
 * overridden for this clinic).
 *
 * Used by:
 *   , admin clinic detail page (Billing & acquisition tab) , to
 *     surface the Custom / Default pill per rate row and let admin
 *     set / edit / remove an override inline.
 *   , agent "Your clinics" card , to display the *effective* rate
 *     per bucket (override when present and active, global rate
 *     otherwise) so the agent sees the real commercial terms for
 *     each of their introduced clinics.
 *
 * RLS narrows the visible rows: admin sees everything; the acquiring
 * agent sees their own clinics' overrides; everyone else sees nothing.
 */
export function clinicCommissionOverridesQuery(
  client: SupabaseClient<Database>,
  opts: { clinicId: string },
) {
  const { clinicId } = opts;
  return {
    queryKey: agentCommissionKeys.clinicOverrides(clinicId),
    queryFn: async (): Promise<ClinicCommissionOverrideRow[]> => {
      const { data, error } = await client
        .from('clinic_commission_overrides')
        .select(
          'clinic_id, bucket, target_price_aed, floor_price_aed, target_commission_aed, active, notes, updated_at, updated_by',
        )
        .eq('clinic_id', clinicId)
        .order('bucket', { ascending: true });
      if (error) throw new Error(error.message);
      return (data ?? []).map((r) => ({
        clinic_id: String(r.clinic_id),
        bucket: r.bucket,
        target_price_aed: Number(r.target_price_aed ?? 0),
        floor_price_aed: Number(r.floor_price_aed ?? 0),
        target_commission_aed: Number(r.target_commission_aed ?? 0),
        active: Boolean(r.active),
        notes: r.notes,
        updated_at: String(r.updated_at),
        updated_by: r.updated_by,
      }));
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  7. agentClinicAgreementsQuery                                             */
/* -------------------------------------------------------------------------- */

/**
 * Read-only fetch of the negotiated deal-sheet rows the logged-in agent
 * has on file for a single clinic, sourced from
 * public.agent_clinic_commission_agreements (introduced in migration
 * 20261122000100, agent SELECT branch added in 20261122000200).
 *
 * Used by the agent dashboard "Your clinics" card to display the
 * negotiated unit price + commission per bucket; falls back to the
 * global rate card when an agreement is absent (merge happens
 * client-side in the card component).
 *
 * RLS narrows the visible rows: admin sees everything, the agent sees
 * only rows where agent_id = auth.uid(). We still pass agentId here so
 * the React Query cache key partitions per-agent (an admin viewing two
 * different agents on the same clinic must not collide). When agentId
 * is null we filter by clinic_id alone and rely entirely on RLS, which
 * is correct for an admin browsing a clinic page without picking an
 * agent first.
 *
 * The agent_clinic_commission_agreements table is not present in the
 * generated Database types yet (commissions.ts uses the same untyped
 * cast pattern). Once types are regenerated the cast can be removed.
 */
export function agentClinicAgreementsQuery(
  client: SupabaseClient<Database>,
  opts: { agentId: string | null; clinicId: string },
) {
  const { agentId, clinicId } = opts;
  return {
    queryKey: agentCommissionKeys.agentClinicAgreements(agentId, clinicId),
    queryFn: async (): Promise<AgentClinicAgreementRateRow[]> => {
      const untyped = client as unknown as SupabaseClient;
      let q = untyped
        .from('agent_clinic_commission_agreements')
        .select(
          'clinic_id, bucket, agreed_price_aed, commission_aed, notes, updated_at',
        )
        .eq('clinic_id', clinicId);
      // Belt + suspenders: RLS already narrows to the caller's own
      // rows, but the spec requires the UI to only ever ask for its
      // own agreements. When agentId is present we add the explicit
      // filter so an admin client (which sees everything) does not
      // accidentally surface another agent's terms on this card.
      if (agentId) q = q.eq('agent_id', agentId);

      const { data, error } = await q;
      if (error) throw new Error(error.message);

      const rows = (data ?? []) as unknown as Array<{
        clinic_id: string;
        bucket: CommissionBucket;
        agreed_price_aed: number | string | null;
        commission_aed: number | string | null;
        notes: string | null;
        updated_at: string;
      }>;
      return rows.map((r) => ({
        clinic_id: String(r.clinic_id),
        bucket: r.bucket,
        agreed_price_aed: Number(r.agreed_price_aed ?? 0),
        commission_aed: Number(r.commission_aed ?? 0),
        notes: r.notes,
        updated_at: String(r.updated_at),
      }));
    },
  };
}
